import {ReactElement, useContext, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {BudgetingType} from '../../../../BudgetingType';
import {
    OriginalRevenueOperationalMetricValidKey,
    VersionType
} from '../../../../__generated__/generated_types';
import {getBudgetYearForProperty, useProperties} from '../../../../contexts/properties/PropertiesContext';
import {useSettings} from '../../../../contexts/settings/SettingsContext';

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as rewriteViewCSS from "./leaseExpirationsRewrite.module.scss";

import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import {Button} from "@zendeskgarden/react-buttons";
import {Body, Close, Header, Modal} from "@zendeskgarden/react-modals";
import Handsontable from 'handsontable';
import "native-injects";
import {ImMagicWand} from 'react-icons/im';
import RevenueWizard from '../../../../components/revenue-wizard/RevenueWizard';
import {IVZHotTableRow} from '../../../../components/vz-ui/vz-hot-table/VZHotTable';
import {VZHotTableConfig} from '../../../../components/vz-ui/vz-hot-table/vz-hot-table-config';
import {defaultSummaryColumnFunction} from '../../../../components/vz-ui/vz-hot-table/vz-hot-table-helpers';
import VZSaveStatus from '../../../../components/vz-ui/vz-save-status/VZSaveStatus';
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {AuthContext} from "../../../../contexts/AuthContext";
import {hideRevenueWizard} from '../../../../global/feature_checkers';
import {getMonths} from '../../../../utils/months-helper';
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import OperationalVZHotTable from '../OperationalVZHotTable';
import {useUTSVOperationalMetrics} from '../hooks/useUTSVOperationalMetrics';
import {OperationalFinancialMetrics} from '../operational-metrics';
import {IUTSVVersion, parseUTSVQueryResponse} from '../utils/operational-metric-helpers';
import {LeaseExpirationsRewriteView} from "./LeaseExpirationsRewriteView";
import {LeaseExpirationCurveModal} from "./curve/LeaseExpirationCurveModal";


export default function LeaseExpirations(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as {type: BudgetingType; propertyId: string};
    const auth = useContext(AuthContext);
    const {currentProperty} = useProperties();
    const {year} = useSettings();

    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const currentVersionYear = type == BudgetingType.BUDGET ? year + 1 : year;
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);
    const isRevWizHidden = hideRevenueWizard(auth.user);

    const {
        fetchMetricData,
        metricData,
        metricDataLoading,
        forecastLocks,
        lastSaved,
        savingValues,
        upsertValues,
    } = useUTSVOperationalMetrics({
        property: currentProperty,
        year: currentVersionYear,
        versionType,
    });

    const [showRewriteModal, setShowRewriteModal] = useState(false);
    const [showExpirationCurveModal, setShowExpirationCurveModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const [wizardOpen, setWizardOpen] = useState(false);

    useEffect(
        () => {
            const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
            if (updateWorkflowTab != false) {
                setSelectedTab(updateWorkflowTab as string);
            }
            fetchMetricData();
        },
        [type]
    );

    // this one has more going on, so for now I'm not trying to force it to work with the buildUTSVTableData method
    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        const tableData: IVZHotTableRow[] = [];
        const tableConfig = new VZHotTableConfig();

        if (!forecastLocks?.forecastLocks || !metricData || !currentProperty) {
            return {tableData, tableConfig};
        }

        const typedData: IUTSVVersion[] = parseUTSVQueryResponse(metricData);
        const versionData = typedData.find(x => x.version == versionType);
        const totalExpirationsByMonth = new Array(12).fill(0);
        let unitCountTotal = 0;

        if (versionData) {
            const sortedUnitTypes = versionData.unitTypes.sort((a, b) => a.unitTypeName.localeCompare(b.unitTypeName));

            for (const unitType of sortedUnitTypes) {
                unitType.leaseExpirationCount.map((x, i) => totalExpirationsByMonth[i] += x);
                unitCountTotal += unitType.unitTypeCount;

                tableData.push({
                    parentRowId: "summary-row",
                    rowType: "child",
                    label: unitType.unitTypeName,
                    values: unitType.leaseExpirationCount,
                    extraData: {
                        unitTypeId: unitType.unitTypeId,
                        unitCount: unitType.unitTypeCount,
                        percentExpiring: unitType.unitTypeCount != null ? +(unitType.leaseExpirationCount.sum() / unitType.unitTypeCount).toFixed(1) : 0,
                    }
                });
            }
        }

        tableData.push({
            label: "Totals",
            rowId: "summary-row",
            rowType: "summary",
            values: totalExpirationsByMonth,
            extraData: {
                unitCount: unitCountTotal,
                percentExpiring: unitCountTotal == null ? null : +(totalExpirationsByMonth.sum() / unitCountTotal * 100).toFixed(1),
            }
        });

        tableData.push({
            label: "Expiring %",
            rowType: "summary",
            values: unitCountTotal ? totalExpirationsByMonth.map(x => +(x / unitCountTotal * 100).toFixed(1)) : new Array(12).fill(0),
            parentRowSummaryFunction: (_row, _col, values) => {
                if (!unitCountTotal) {
                    return 0;
                }
                if (unitCountTotal > 0) {
                    return +(values.sum() / unitCountTotal * 100).toFixed(1);
                }

                return 0;
            },
        });

        let tableLocked = true;
        if (type == BudgetingType.BUDGET) {
            tableLocked = forecastLocks?.forecastLocks?.property.budgetLocked;
        } else {
            tableLocked = forecastLocks?.forecastLocks?.property.reforecastLocked;
        }

        let writeableColumnsStart = 1;

        if (type == BudgetingType.REFORECAST) {
            writeableColumnsStart = currentProperty.reforecastStartMonthIndex + 1;
        }

        tableConfig.tableSummaryFunction = "sum";
        tableConfig.applyToColumnAndFollowing(writeableColumnsStart, {isReadOnly: tableLocked, format: "count"});
        tableConfig.applyToColumnAndFollowing(13, {isReadOnly: true, isGreyBackground: true, isBoldText: true});
        tableConfig.applyToRow(tableData.length - 2, {isReadOnly: true, isGreyBackground: true, isThickBorderTop: true, isBoldText: true});
        tableConfig.applyToRow(tableData.length - 1, {format: "percent", isReadOnly: true, isGreyBackground: true, isBoldText: true});
        tableConfig.applyToColumn(15, {format: "percent"});
        tableConfig.applyToColumn(0, {isRowHeader: true, isBoldText: true, format: "string"});

        // for the empty cells at the end of this table
        tableConfig.applyConfig({
            rowStart: tableData.length - 1,
            rowEnd: tableData.length - 1,
            colStart: 13,
            colEnd: 15,
            config: {format: "string"},
        });

        if (type == BudgetingType.REFORECAST) {
            tableConfig.push({
                rowStart: 0,
                rowEnd: tableData.filter(x => x.rowType != "summary").length - 1,
                colStart: currentProperty.reforecastStartMonthIndex + 1,
                colEnd: currentProperty.reforecastStartMonthIndex + 1,
                config: {
                    isThickBorderLeft: true,
                },
            });
        }

        return {tableData, tableConfig};
    }, [metricData, forecastLocks?.forecastLocks, currentProperty]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow} />
                            <WorkflowPageLabel label="Lease Expiration Schedule" />
                            {currentProperty?.id && type && forecastLocks?.forecastLocks && !currentProperty.isAcquisitionProperty &&
                                <Button
                                    onClick={() => setShowRewriteModal(true)}
                                    disabled={type === BudgetingType.REFORECAST ? forecastLocks.forecastLocks.property.reforecastLocked : forecastLocks.forecastLocks.property.budgetLocked}
                                >
                                    Sync With PMS
                                </Button>
                            }
                            {currentProperty?.id && type === BudgetingType.BUDGET &&
                                <Button onClick={() => setShowExpirationCurveModal(true)}>Use Expiration Curve</Button>
                            }
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved} />
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow} />
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>

                <div className={workflowCSS.bodyContainer}>
                    {currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <OperationalVZHotTable
                                    data={tableData}
                                    colWidths={[80]}
                                    colHeaders={["Unit Type", ...getMonths(currentProperty), "Expirations", "Unit Count", "Expiring %"]}
                                    fixedRowsBottom={2}
                                    handsontableProps={{
                                        columns: [
                                            {data: "label"},
                                            {data: `values.0`, type: "numeric"},
                                            {data: `values.1`, type: "numeric"},
                                            {data: `values.2`, type: "numeric"},
                                            {data: `values.3`, type: "numeric"},
                                            {data: `values.4`, type: "numeric"},
                                            {data: `values.5`, type: "numeric"},
                                            {data: `values.6`, type: "numeric"},
                                            {data: `values.7`, type: "numeric"},
                                            {data: `values.8`, type: "numeric"},
                                            {data: `values.9`, type: "numeric"},
                                            {data: `values.10`, type: "numeric"},
                                            {data: `values.11`, type: "numeric"},
                                            {
                                                data(row: Handsontable.RowObject) {
                                                    const rowData = row as IVZHotTableRow;
                                                    if (tableData.indexOf(rowData) == tableData.length - 1) {
                                                        return null;
                                                    } else {
                                                        return defaultSummaryColumnFunction(tableData, tableConfig, row);
                                                    }
                                                }
                                            },
                                            {data: `extraData.unitCount`, type: "numeric"},
                                            {
                                                data(row: Handsontable.RowObject) {
                                                    const rowData = row as IVZHotTableRow;
                                                    const extraData = rowData?.extraData;

                                                    if (extraData) {
                                                        const unitCount = extraData["unitCount"] ?? null;

                                                        if (typeof unitCount == "number" && unitCount > 0) {
                                                            return +(rowData.values.sum() / unitCount * 100).toFixed(1);
                                                        }
                                                    }

                                                    return null;
                                                }
                                            },
                                        ],
                                    }}
                                    tableConfig={tableConfig}
                                    isDataLoading={metricDataLoading}
                                    isSearchable
                                    isUndoRedoable
                                    onChange={(changes) => upsertValues(tableData, changes, budgetYear, OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount)}
                                    customControls={
                                        currentProperty.isAcquisitionProperty ||
                                        !forecastLocks?.forecastLocks ||
                                        forecastLocks?.forecastLocks?.property.reforecastLocked ||
                                        forecastLocks?.forecastLocks?.property.budgetLocked ||
                                        isRevWizHidden ?
                                        <></>
                                        :
                                        <Button isPrimary onClick={() => setWizardOpen(true)}>
                                            <Button.StartIcon><ImMagicWand /></Button.StartIcon>
                                            Revenue Wizard
                                        </Button>
                                    }
                                />
                                {!currentProperty?.isAcquisitionProperty &&
                                    <OperationalFinancialMetrics type={type} budgetYear={budgetYear} />
                                }
                            </div>
                        </>
                    }
                </div>
            </div>

            {showRewriteModal && currentProperty && type &&
                <Modal onClose={() => setShowRewriteModal(false)} isLarge className={rewriteViewCSS.valuesModal}>
                    <Header>
                        Sync PMS values to your assumptions for {type == BudgetingType.BUDGET ? currentProperty.budgetYear : currentProperty.reforecastYear} {type}
                    </Header>
                    <Body className={rewriteViewCSS.valuesModalBody}>
                        <LeaseExpirationsRewriteView
                            propertyId={currentProperty.id}
                            budgetYear={currentProperty.budgetYear}
                            versionType={type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast}
                            valuesStartFromIndex={type == BudgetingType.BUDGET ? 0 : currentProperty.reforecastStartMonthIndex}
                            onCancel={() => setShowRewriteModal(false)}
                            onApply={() => {setShowRewriteModal(false); fetchMetricData();}}
                        />
                    </Body>
                    <Close aria-label="Close modal" />
                </Modal>
            }
            {showExpirationCurveModal && currentProperty &&
                <LeaseExpirationCurveModal
                    property={currentProperty}
                    budgetYear={currentProperty.reforecastYear + 1}
                    handleCancel={() => setShowExpirationCurveModal(false)}
                    handleConfirm={() => {
                        setShowExpirationCurveModal(false);
                        fetchMetricData();
                    }}
                />
            }

            {
                metricData &&
                currentProperty &&
                !currentProperty?.isAcquisitionProperty &&
                !forecastLocks?.forecastLocks?.property.reforecastLocked &&
                !forecastLocks?.forecastLocks?.property.budgetLocked &&
                !isRevWizHidden &&
                    <RevenueWizard
                        property={currentProperty}
                        budgetYear={budgetYear}
                        isOpen={wizardOpen}
                        metricData={metricData}
                        onClose={dataSaved => {
                            setWizardOpen(false);

                            if (dataSaved) {
                                fetchMetricData();
                            }
                        }}
                    />
            }
        </ThemeProvider>
    );
}
